import { LinearGradient } from 'expo-linear-gradient';
import withLink from 'hoc/withLink';
import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import { memo, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { calcHeightWithWidth, calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import TitleCard from './TitleCard';

import { EventDateTag } from 'components/EventDateTag';
import FastImage from 'components/FastImage';

/**
 * [MIGRAÇÃO] O hook useBreakpointValueGS foi migrado para o novo padrão de flags booleanas.
 * Agora, a lógica de responsividade é feita via condicionais usando as flags retornadas pelo hook.
 */
const BigCard: React.FC<CardProps> = ({ title, tag, uri, jsxButton, lines, ...rest }) => {
  const bigCardAspectRatio = 9 / 5;
  const overrideBaseWidth = rest.override?.cardWidth?.base;
  const overrideSmWidth = rest.override?.cardWidth?.sm || rest.override?.cardWidth?.base;
  const overrideXlWidth = rest.override?.cardWidth?.xl || rest.override?.cardWidth?.base;
  const itemStartDate = rest.startDate ? new Date(rest.startDate) : null;

  const bigCardWidth = useBreakpointValueGS({
    xl: overrideXlWidth || calcSizeForDevice(370, 550),
    sm: overrideSmWidth || (Platform.isAndroidTV ? 275 : 333),
    base: overrideBaseWidth || rest.override?.cardWidth?.base || 280,
  });

  const cardInfoPadding = useBreakpointValueGS({
    xl: calcSizeForDevice(15),
    lg: 15,
    base: 12,
  });

  const textSize = useBreakpointValueGS({
    xl: calcSizeForDevice(20, 24),
    lg: 20,
    base: Platform.isAndroidTV ? calcSizeForDevice(24) : 16,
  });

  const lineHeight = useBreakpointValueGS({
    xl: calcSizeForDevice(24, 32),
    lg: 24,
    base: Platform.isAndroidTV ? calcSizeForDevice(32) : 20,
  });

  const eventTagTextSize = useBreakpointValueGS({
    lg: calcSizeForDevice(24, 32),
    base: Platform.isAndroidTV ? calcSizeForDevice(32) : 14,
  });

  const bigCardHeight = useMemo(
    () => calcHeightWithWidth(bigCardWidth ?? 280, bigCardAspectRatio),
    [bigCardWidth, bigCardAspectRatio]
  );

  return (
    <View style={[styles.card, { width: bigCardWidth, height: bigCardHeight }]}>
      {uri ? (
        <>
          <FastImage
            size="md"
            aspect="16x9"
            source={{ uri }}
            style={{ width: '100%', height: '100%', borderRadius: 8, overflow: 'hidden' }}
          />

          {(Boolean(title) || Boolean(tag)) && (
            <View
              style={[
                styles.overlay,
                {
                  padding: cardInfoPadding,
                },
              ]}>
              {tag && (
                <View style={styles.tagBox}>
                  <Text style={[styles.tagText, { fontSize: eventTagTextSize }]}>{tag}</Text>
                </View>
              )}
              {Boolean(title) && (
                <Text
                  style={[styles.titleText, { fontSize: textSize, lineHeight: lineHeight }]}
                  numberOfLines={lines?.title}>
                  {title}
                </Text>
              )}
            </View>
          )}

          {itemStartDate && <EventDateTag startDate={itemStartDate} />}

          {Boolean((rest as any)?.item?.percentageWatched) && (
            <LinearGradient
              start={{ x: 0, y: 1 }}
              end={{ x: 0, y: 0 }}
              locations={[0.1, 0.95]}
              style={{
                zIndex: 1,
                width: '100%',
                height: '100%',
                position: 'absolute',
                padding: cardInfoPadding,
              }}
              colors={['#00000080', 'transparent']}>
              <View
                style={[
                  styles.progressBarContainer,
                  {
                    borderBottomLeftRadius: calcSizeForDevice(3),
                    borderBottomRightRadius: calcSizeForDevice(3),
                    height: calcSizeForDevice(4),
                    marginTop: 'auto',
                  },
                ]}>
                <View
                  style={[
                    styles.progressBar,
                    {
                      height: calcSizeForDevice(4),
                      borderBottomLeftRadius: calcSizeForDevice(3),
                      width: `${(rest as any).item.percentageWatched}%`,
                    },
                  ]}
                />
              </View>
            </LinearGradient>
          )}

          {Boolean(jsxButton) && (
            <LinearGradient
              start={{ x: 0, y: 1 }}
              end={{ x: 0, y: 0 }}
              locations={[0.1, 0.95]}
              style={{
                zIndex: 1,
                width: '100%',
                height: '100%',
                position: 'absolute',
                padding: calcSizeForDevice(15),
              }}
              colors={['#000000E6', 'transparent']}
            />
          )}
        </>
      ) : (
        <TitleCard title={title || rest?.imgTitle} reference={rest?.reference} />
      )}
    </View>
  );
};

const BigCardWithLink = memo(withLink(BigCard, 'BigCard'));

const BigCardProxy: React.FC<CardProps> = (props) => {
  if (!props.jsxButton) return <BigCardWithLink {...props} />;

  return (
    <View style={{ position: 'relative' }}>
      <BigCardWithLink {...props} />

      <View
        style={{
          marginTop: 'auto',
          position: 'absolute',
          alignSelf: 'flex-end',
          bottom: calcSizeForDevice(15),
          right: calcSizeForDevice(15),
        }}>
        {props.jsxButton}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#070d31',
  },
  overlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  tagBox: {
    borderWidth: 1,
    borderColor: 'white',
    borderRadius: 5,
    paddingHorizontal: 5,
    marginBottom: 5,
    alignSelf: 'flex-start',
    backgroundColor: 'transparent',
  },
  tagText: {
    color: 'white',
    fontWeight: '600',
  },
  titleText: {
    fontWeight: '700',
    color: 'white',
  },
  progressBarContainer: {
    backgroundColor: 'rgba(217, 217, 217, 0.33)',
    width: '100%',
    flexDirection: 'row',
  },
  progressBar: {
    backgroundColor: 'rgba(217, 217, 217, 1)',
  },
});

export default BigCardProxy;
